// Per-match state machine: timer, scores, plane lifecycle, events.

import type { Plane, Team } from '../plane/Plane';

export type MatchPhase = 'pre' | 'live' | 'post';

export interface KillRecord {
  victim: string;
  victimTeam: Team;
  killer?: string;
  killerTeam?: Team;
  ts: number;
}

export interface ScoreSnapshot {
  blue: number;
  red: number;
  timeLeft: number;
  alive: { blue: number; red: number };
}

const MATCH_DURATION = 180;

export class GameState {
  phase: MatchPhase = 'pre';
  startedAt = 0;
  now = 0;
  scoreBlue = 0;
  scoreRed = 0;
  kills: KillRecord[] = [];
  winner: Team | 'draw' | null = null;

  start(now: number): void {
    this.phase = 'live';
    this.startedAt = now;
    this.now = now;
  }

  step(now: number): void {
    this.now = now;
    if (this.phase === 'live' && this.timeLeft <= 0) this.phase = 'post';
  }

  recordKill(rec: KillRecord, planes: Plane[]): void {
    this.kills.push(rec);
    if (rec.killerTeam === 'blue') this.scoreBlue++;
    else if (rec.killerTeam === 'red') this.scoreRed++;
    // Side-elimination win: any team with zero alive ends the match early.
    const alive = this.aliveCounts(planes);
    if (alive.blue === 0 || alive.red === 0) {
      this.phase = 'post';
      this.winner = alive.blue > alive.red ? 'blue' : alive.red > alive.blue ? 'red' : 'draw';
    }
  }

  aliveCounts(planes: Plane[]): { blue: number; red: number } {
    let b = 0, r = 0;
    for (const p of planes) {
      if (!p.alive) continue;
      if (p.team === 'blue') b++; else r++;
    }
    return { blue: b, red: r };
  }

  get timeLeft(): number {
    return Math.max(0, MATCH_DURATION - (this.now - this.startedAt));
  }

  snapshot(planes: Plane[]): ScoreSnapshot {
    return { blue: this.scoreBlue, red: this.scoreRed, timeLeft: this.timeLeft, alive: this.aliveCounts(planes) };
  }

  finalize(planes: Plane[]): void {
    if (this.winner) return;
    const a = this.aliveCounts(planes);
    if (a.blue > a.red) this.winner = 'blue';
    else if (a.red > a.blue) this.winner = 'red';
    else if (this.scoreBlue > this.scoreRed) this.winner = 'blue';
    else if (this.scoreRed > this.scoreBlue) this.winner = 'red';
    else this.winner = 'draw';
  }
}

export const MATCH = { DURATION: MATCH_DURATION };
